import React from 'react';
import { I18n } from 'react-redux-i18n';
import { Link } from 'react-router-dom';

import { CONTENT_MODERATION_ROUTES, I18N_BASE } from '../../constants';
import { QueueListItem } from '../../models';
import { subjectIdSegment } from '../../helpers/subjectId';
import AgeDisplay from '../../components/AgeDisplay/AgeDisplay';
import CategoryChip from '../../components/CategoryChip/CategoryChip';
import LockedByBadge from '../../components/LockedByBadge/LockedByBadge';
import ScorePill from '../../components/ScorePill/ScorePill';
import TriggerBadge from '../../components/TriggerBadge/TriggerBadge';
import styles from './queue.module.css';

export type QueueCardVariant = 'available' | 'lockedByMe' | 'lockedByOther';

export interface QueueCardProps {
  item: QueueListItem;
  currentReviewerAccountId: number | null;
  testId?: string;
}

export function resolveVariant(
  item: QueueListItem,
  currentReviewerAccountId: number | null,
): QueueCardVariant {
  if (item.lockedByReviewerAccountId == null) return 'available';
  if (
    currentReviewerAccountId != null &&
    item.lockedByReviewerAccountId === currentReviewerAccountId
  ) {
    return 'lockedByMe';
  }
  return 'lockedByOther';
}

function fallbackTitle(item: QueueListItem): string {
  return I18n.t(`${I18N_BASE}.queue.card.fallbackTitle`, {
    kind: I18n.t(`${I18N_BASE}.subjectKind.${item.subjectKind}`),
    id: item.contentModerationEventId,
  });
}

// Re-export from the shared helper so existing tests that import from this
// module keep working. New callers should import from helpers/subjectId.
export { subjectIdSegment };

function ctaKey(variant: QueueCardVariant): string {
  switch (variant) {
    case 'available':
      return `${I18N_BASE}.queue.card.cta.review`;
    case 'lockedByMe':
      return `${I18N_BASE}.queue.card.cta.resume`;
    case 'lockedByOther':
      return `${I18N_BASE}.queue.card.cta.locked`;
  }
}

export function QueueCard({
  item,
  currentReviewerAccountId,
  testId,
}: QueueCardProps): React.ReactElement {
  const variant = resolveVariant(item, currentReviewerAccountId);
  const interactive = variant !== 'lockedByOther';

  const className = [
    styles.card,
    variant === 'lockedByMe' ? styles.cardLockedByMe : '',
    variant === 'lockedByOther' ? styles.cardLockedByOther : '',
  ]
    .filter(Boolean)
    .join(' ');

  const title = item.pageNameSnapshot ?? fallbackTitle(item);
  // The card body is the same regardless of variant — only the wrapper
  // element changes (Link vs. div). Extracting it keeps the variant branch
  // below a single conditional rather than two parallel trees.
  const body = (
    <>
      <ScorePill score={item.aiScore} />
      <div className={styles.cardMain}>
        <h3 className={styles.cardTitle}>{title}</h3>
        <span className={styles.cardSubtitle}>
          {(() => {
            const subject = subjectIdSegment(item);
            return subject ? `${I18n.t(subject.i18nKey, { id: subject.id })} · ` : '';
          })()}
          {I18n.t(`${I18N_BASE}.queue.card.subtitle`, {
            account: item.linkedAccountId ?? '?',
            org: item.linkedOrganizationId ?? '?',
          })}
        </span>
        <div className={styles.cardMeta}>
          <CategoryChip category={item.aiCategory} />
          <TriggerBadge trigger={item.triggerReason} />
          <AgeDisplay timestamp={item.triggeredAt} />
          {item.activeDecision ? (
            <span
              className={`${styles.decisionBadge} ${
                item.activeDecision.decision === 'block'
                  ? styles.decisionBlock
                  : styles.decisionApprove
              }`}
              data-testid={`queue-card-${item.contentModerationEventId}-decision`}
              data-decision={item.activeDecision.decision}
            >
              {I18n.t(
                `${I18N_BASE}.queue.card.decision.${item.activeDecision.decision}`,
              )}
              {item.activeDecision.isAutomated ? (
                <span className={styles.decisionAgreed}>
                  {I18n.t(`${I18N_BASE}.queue.card.decision.auto`)}
                </span>
              ) : item.activeDecision.agreedWithAi ? (
                <span className={styles.decisionAgreed}>
                  {I18n.t(`${I18N_BASE}.queue.card.decision.agreedShort`)}
                </span>
              ) : null}
            </span>
          ) : null}
        </div>
      </div>
      <div className={styles.cardCta}>
        {variant === 'lockedByOther' ? (
          <LockedByBadge
            reviewerName={null}
            lastHeartbeatAt={item.lockLastHeartbeatAt}
          />
        ) : null}
        <span>{I18n.t(ctaKey(variant))}</span>
      </div>
    </>
  );

  // Interactive variants render as a real <a href> so the browser handles
  // Cmd/Ctrl-click, middle-click, right-click → "Open in new tab", and link
  // preview natively. lockedByOther stays a non-interactive <div>.
  if (interactive) {
    return (
      <Link
        to={CONTENT_MODERATION_ROUTES.detail(item.contentModerationEventId)}
        className={className}
        data-testid={testId}
        data-variant={variant}
      >
        {body}
      </Link>
    );
  }
  return (
    <div
      className={className}
      data-testid={testId}
      data-variant={variant}
      aria-disabled="true"
    >
      {body}
    </div>
  );
}

export default QueueCard;
